import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';
import { formatCents, todayYmd, splitServiceName } from '../lib/format';
import { useTenant } from '../lib/tenantContext';
import { IntakeFieldInput } from '../components/IntakeFieldInput';

interface Service {
  id: number;
  name: string;
  duration_min: number;
  price_cents: number;
  color: string | null;
  sort_order: number;
}

interface Customer {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  total_visits: number;
}

export function NewBooking() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1: customer
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newMetadata, setNewMetadata] = useState<Record<string, unknown>>({});

  // Step 2: service
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Step 3: time + notes + intake + assignment
  const [date, setDate] = useState(todayYmd());
  const [time, setTime] = useState('14:00');
  const [source, setSource] = useState<'walk_in' | 'phone'>('phone');
  const [notes, setNotes] = useState('');
  const [intakeData, setIntakeData] = useState<Record<string, unknown>>({});
  const [stationId, setStationId] = useState<number | null>(null);
  const [assignedToId, setAssignedToId] = useState<number | null>(null);
  const [stations, setStations] = useState<Array<{ id: number; name: string; color: string | null }>>([]);
  const [staff, setStaff] = useState<Array<{ id: number; display_name: string; role: string }>>([]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const customerFields = tenant?.customer_fields ?? [];
  const bookingFields = tenant?.booking_fields ?? [];
  const showAssign = stations.length > 0 || staff.length > 1;

  useEffect(() => {
    apiGet<{ services: Service[] }>('/api/services')
      .then((r) => setServices(r.services))
      .catch((e) => setErr((e as Error).message));
    // Stations + staff load in parallel; failures are non-fatal (assignment
    // is optional, so we just don't show the picker if either fails).
    apiGet<{ stations: Array<{ id: number; name: string; color: string | null }> }>('/api/stations')
      .then((r) => setStations(r.stations))
      .catch(() => {});
    apiGet<{ staff: Array<{ id: number; display_name: string; role: string }> }>('/api/stations/staff')
      .then((r) => setStaff(r.staff))
      .catch(() => {});
  }, []);

  // Customer search debounce
  useEffect(() => {
    const t = setTimeout(async () => {
      if (search.trim().length === 0) {
        setResults([]);
        return;
      }
      try {
        const r = await apiGet<{ customers: Customer[] }>(`/api/customers?search=${encodeURIComponent(search.trim())}`);
        setResults(r.customers);
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  // Group services by base name (e.g., "Bath" → all weight tiers)
  const serviceGroups = useMemo(() => {
    const groups: Record<string, Service[]> = {};
    for (const s of services) {
      const { base } = splitServiceName(s.name);
      (groups[base] ||= []).push(s);
    }
    Object.values(groups).forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));
    return groups;
  }, [services]);

  async function submit() {
    if (!selectedService) return;
    setErr(null);
    setBusy(true);
    try {
      // For brand-new customers we need to POST /api/customers first so
      // metadata lands on the customer row (the bookings inline-customer
      // path doesn't take metadata). Existing customers keep their meta.
      let customerId: number | null = selectedCustomer?.id ?? null;
      if (!customerId && newName.trim()) {
        const cleanedMeta: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(newMetadata)) {
          if (v === undefined || v === null || v === '') continue;
          cleanedMeta[k] = v;
        }
        const cr = await apiPost<{ customer: { id: number } }>(
          '/api/customers',
          {
            name: newName.trim(),
            phone: newPhone.trim() || undefined,
            email: newEmail.trim() || undefined,
            metadata: cleanedMeta,
          }
        );
        customerId = cr.customer.id;
      }

      const cleanedIntake: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(intakeData)) {
        if (v === undefined || v === null || v === '') continue;
        cleanedIntake[k] = v;
      }

      const payload: Record<string, unknown> = {
        service_id: selectedService.id,
        start_at: `${date} ${time}:00`,
        source,
        operator_notes: notes.trim() || undefined,
        intake_data: cleanedIntake,
      };
      if (customerId) payload.customer_id = customerId;
      if (stationId) payload.station_id = stationId;
      if (assignedToId) payload.assigned_to_user_id = assignedToId;

      await apiPost('/api/bookings', payload);
      navigate('/', { replace: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const customerReady = !!(selectedCustomer || newName.trim());

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Step n={1} active={step === 1} done={step > 1} label="Customer" />
        <Bar />
        <Step n={2} active={step === 2} done={step > 2} label="Service" />
        <Bar />
        <Step n={3} active={step === 3} done={false} label="Time" />
      </div>

      {err && <p className="text-rose-400">{err}</p>}

      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="label">Search returning customer</label>
            <input
              className="input"
              placeholder="Name, phone, or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedCustomer(null);
              }}
            />
            {results.length > 0 && (
              <div className="mt-2 space-y-1">
                {results.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setSelectedCustomer(c);
                      setSearch(c.name);
                      setResults([]);
                    }}
                    className="w-full text-left card hover:border-accent"
                  >
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-sm text-muted">
                      <span className="font-mono">{c.phone || c.email || '—'}</span> · <span className="font-mono">{c.total_visits}</span> visits
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selectedCustomer && (
              <div className="mt-2 text-sm text-emerald-300">✓ {selectedCustomer.name} selected</div>
            )}
          </div>

          {!selectedCustomer && (
            <>
              <div className="text-center text-muted text-sm">— or new customer —</div>
              <div className="space-y-3">
                <div>
                  <label className="label">Name</label>
                  <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                </div>
                <div>
                  <label className="label">Email (optional)</label>
                  <input className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                </div>
                {customerFields.map((f) => (
                  <IntakeFieldInput
                    key={f.key}
                    field={f}
                    value={newMetadata[f.key]}
                    onChange={(v) =>
                      setNewMetadata((prev) => ({ ...prev, [f.key]: v }))
                    }
                  />
                ))}
              </div>
            </>
          )}

          <button
            onClick={() => setStep(2)}
            disabled={!customerReady}
            className="btn-primary w-full disabled:opacity-40"
          >
            Next → Service
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {Object.entries(serviceGroups).map(([base, opts]) => (
            <div key={base} className="card space-y-2">
              <h3 className="font-semibold">{base}</h3>
              <div className="grid grid-cols-2 gap-2">
                {opts.map((s) => {
                  const { tier } = splitServiceName(s.name);
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSelectedService(s)}
                      className={`p-3 rounded-lg border text-left ${
                        selectedService?.id === s.id
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent-2'
                      }`}
                    >
                      <div className="font-semibold text-sm">{tier || s.name}</div>
                      <div className="text-xs text-muted"><span className="font-mono">{s.duration_min}</span> min · <span className="font-mono">{formatCents(s.price_cents)}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setStep(1)} className="btn-ghost">← Back</button>
            <button
              onClick={() => setStep(3)}
              disabled={!selectedService}
              className="btn-primary disabled:opacity-40"
            >
              Next → Time
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <p className="text-sm text-muted">
              {selectedCustomer?.name || newName} · {selectedService?.name} · <span className="font-mono">{formatCents(selectedService?.price_cents || 0)}</span>
            </p>

            <div>
              <label className="label">Date</label>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Time</label>
              <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div>
              <label className="label">Source</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setSource('phone')}
                  className={`p-3 rounded-lg border ${source === 'phone' ? 'border-accent bg-accent/10' : 'border-border'}`}
                >
                  Phone
                </button>
                <button
                  onClick={() => setSource('walk_in')}
                  className={`p-3 rounded-lg border ${source === 'walk_in' ? 'border-accent bg-accent/10' : 'border-border'}`}
                >
                  Walk-in
                </button>
              </div>
            </div>
            <div>
              <label className="label">Notes (optional)</label>
              <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {bookingFields.length > 0 && (
            <div className="card space-y-3">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted">
                Intake
              </h3>
              {bookingFields.map((f) => (
                <IntakeFieldInput
                  key={f.key}
                  field={f}
                  value={intakeData[f.key]}
                  onChange={(v) =>
                    setIntakeData((prev) => ({ ...prev, [f.key]: v }))
                  }
                />
              ))}
            </div>
          )}

          {/* Assignment — only renders for tenants with stations or multi-staff */}
          {showAssign && (
            <div className="card space-y-3">
              <h3 className="font-semibold text-sm uppercase tracking-wide text-muted">
                Assign
              </h3>
              {stations.length > 0 && (
                <div>
                  <label className="label">Station</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setStationId(null)}
                      className={`p-2 rounded-lg border text-sm ${stationId === null ? 'border-accent bg-accent/10' : 'border-border text-muted'}`}
                    >
                      &mdash;
                    </button>
                    {stations.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setStationId(s.id)}
                        className={`p-2 rounded-lg border text-sm flex items-center gap-2 ${stationId === s.id ? 'border-accent bg-accent/10' : 'border-border'}`}
                      >
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color || 'var(--accent)' }} />
                        <span className="truncate">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {staff.length > 1 && (
                <div>
                  <label className="label">Groomer / staff</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setAssignedToId(null)}
                      className={`p-2 rounded-lg border text-sm ${assignedToId === null ? 'border-accent bg-accent/10' : 'border-border text-muted'}`}
                    >
                      Unassigned
                    </button>
                    {staff.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setAssignedToId(u.id)}
                        className={`p-2 rounded-lg border text-sm truncate ${assignedToId === u.id ? 'border-accent bg-accent/10' : 'border-border'}`}
                      >
                        {u.display_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setStep(2)} className="btn-ghost">← Back</button>
            <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-40">
              {busy ? 'Saving…' : 'Save booking'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  const cls = done
    ? 'bg-emerald-500 text-white border-emerald-500'
    : active
    ? 'bg-accent text-bg border-accent'
    : 'bg-panel text-muted border-border';
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-bold font-mono ${cls}`}>
        {done ? '✓' : n}
      </span>
      <span className={active ? 'text-text font-semibold' : 'text-muted'}>{label}</span>
    </div>
  );
}
function Bar() { return <div className="flex-1 h-px bg-border" />; }
